import React, { useEffect, useState } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Quote, Star, Building, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const CustomerTestimonials = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const testimonials = [
    {
      quote: "BCS has been our go-to contractor for equipment repairs and protective coatings for over 10 years. Their quality of work and reliability are unmatched in the industry.",
      title: "Maintenance Manager",
      company: "Major Oil Refinery",
      industry: "Oil & Gas",
      rating: 5
    },
    {
      quote: "When our critical pump failed during a holiday weekend, BCS had a crew on site within hours. They got us back up and running before Monday morning shift.",
      title: "Plant Engineer",
      company: "Chemical Processing Facility",
      industry: "Petrochemical",
      rating: 5
    },
    {
      quote: "The tank lining job BCS did for us has performed flawlessly for 8 years now. Their attention to surface prep and application is evident in the longevity of their work.",
      title: "Operations Director",
      company: "Water Treatment District",
      industry: "Water & Wastewater",
      rating: 5
    },
    {
      quote: "We've worked with many contractors over the years, but BCS stands out for their professionalism, technical knowledge, and commitment to safety.",
      title: "Facilities Manager",
      company: "Food Processing Plant",
      industry: "Food & Beverage",
      rating: 5
    },
    {
      quote: "BCS's SMART preventive maintenance program has reduced our unplanned downtime by over 60%. The ROI has been exceptional.",
      title: "VP of Operations",
      company: "Manufacturing Corporation",
      industry: "Manufacturing",
      rating: 5
    },
    {
      quote: "Their floor coating system has transformed our warehouse. It's durable, easy to clean, and looks great even after heavy forklift traffic.",
      title: "Distribution Center Manager",
      company: "Logistics Company",
      industry: "General Industry",
      rating: 5
    }
  ];

  const stats = [
    { value: '98%', label: 'Customer Satisfaction' },
    { value: '95%', label: 'Repeat Customers' },
    { value: '100+', label: 'Active Clients' },
    { value: '4.9/5', label: 'Average Rating' }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["about/testimonials"]?.title} description={seoData["about/testimonials"]?.description} path="/about/testimonials" keywords={seoData["about/testimonials"]?.keywords || []} />
      
      <PageHero
        badge="About BCS"
        title="Customer Testimonials"
        description="Don't just take our word for it. Hear what our customers have to say about working with BCS."
        backgroundKey="customer-testimonials"
        showButton={false}
      />

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-green-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-green-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">What Our Clients Say</h2>
          
          <Card className="relative overflow-hidden border-none shadow-xl">
            <div className="absolute top-0 left-0 w-full h-2 bg-green-700"></div>
            <CardContent className="p-8 md:p-12">
              <Quote className="text-green-700 mb-6" size={48} />
              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                "{testimonials[activeTestimonial].quote}"
              </p>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex mb-2">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-500 fill-yellow-500" size={20} />
                    ))}
                  </div>
                  <p className="font-bold text-slate-900">{testimonials[activeTestimonial].title}</p>
                  <p className="text-gray-500 text-sm">{testimonials[activeTestimonial].company}</p>
                </div>
                <Badge className="bg-blue-100 text-blue-900">
                  {testimonials[activeTestimonial].industry}
                </Badge>
              </div>
              
              {/* Navigation */}
              <div className="flex justify-center gap-4 mt-8">
                <Button variant="outline" size="icon" onClick={prevTestimonial}>
                  <ChevronLeft size={20} />
                </Button>
                <div className="flex items-center gap-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === activeTestimonial ? 'bg-green-700' : 'bg-gray-300'
                      }`}
                      onClick={() => setActiveTestimonial(index)}
                    />
                  ))}
                </div>
                <Button variant="outline" size="icon" onClick={nextTestimonial}>
                  <ChevronRight size={20} />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">More Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="h-full">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-500 fill-yellow-500" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 text-sm italic">"{testimonial.quote}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-slate-900 text-sm">{testimonial.title}</p>
                    <p className="text-gray-500 text-xs">{testimonial.company}</p>
                    <div className="flex items-center mt-2">
                      <Building className="text-gray-400 mr-1" size={14} />
                      <span className="text-gray-500 text-xs">{testimonial.industry}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <MessageSquare className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Ready to Join Our Satisfied Customers?</h2>
          <p className="text-blue-100 mb-8">
            Experience the BCS difference for yourself. Contact us today to discuss your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
              Get a Free Quote <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/about/why-choose-bcs')}>
              Why Choose BCS?
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomerTestimonials;
